import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';

// UseCases
import { createValidationSchema } from './tableBatchProcessingUseCase';

/**
 * @typedef useTableBatchProcessingReturn
 * @property {(force?: boolean) => boolean} handleCloseBatchProcessing
 * @property {boolean} batchProcessingIsOpen
 * @property {(isOpen: boolean) => void} setBatchProcessingIsOpen
 * @property {import('react-hook-form').UseFormReturn<{ actions: RowBatchItem[] }>} formBatchProcessing
 * @property {() => RowBatchItem} handleCreateNewRowBatchItem
 */

/**
 * @param {{
 *  batchProcessingConfirmationLabel: string,
 * }} props - The props
 * @returns {useTableBatchProcessingReturn} The useTableBatchProcessing return
 */
const useTableBatchProcessing = ({
  batchProcessingConfirmationLabel = '',
}) => {
  // Form for batch processing
  /** @type {RowBatchItem} */
  const rowBatchInitial = {
    id: '',
    lib: '',
    type: '',
    value: '',
  };
  /** @type {import('react-hook-form').UseFormReturn<{ actions: RowBatchItem[] }>} */
  const formBatchProcessing = useForm({
    defaultValues: {
      actions: [rowBatchInitial],
    },
    resolver: zodResolver(createValidationSchema()),
    mode: 'onChange',
  });

  // Batch Processing
  const [batchProcessingIsOpen, setBatchProcessingIsOpen] = useState(false);

  /**
   * Handle open batch processing
   */
  const handleOpenBatchProcessing = () => {
    setBatchProcessingIsOpen(!batchProcessingIsOpen);

    // Reset form when closing batch processing
    if (batchProcessingIsOpen) {
      setTimeout(() => {
        formBatchProcessing.reset();
      }, 300);
    }
  };

  // Handlers
  /**
   * Handle create new row batch item
   * @returns {RowBatchItem} The new row batch item
   */
  const handleCreateNewRowBatchItem = () => (rowBatchInitial);

  /**
   * Handle close batch processing
   * @param {boolean} force - The force
   * @returns {boolean} has been closed
   */
  const handleCloseBatchProcessing = (force = false) => {
    const isFormTouched = formBatchProcessing.formState.isDirty;
    if (isFormTouched && !force) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(batchProcessingConfirmationLabel);
      if (confirmed) {
        handleOpenBatchProcessing();
        return true;
      }
    } else {
      handleOpenBatchProcessing();
      return true;
    }

    return false;
  };

  return ({
    handleCloseBatchProcessing,
    batchProcessingIsOpen,
    setBatchProcessingIsOpen,

    // Form for batch processing
    formBatchProcessing,

    // Handlers
    handleCreateNewRowBatchItem,
  });
};

export default useTableBatchProcessing;
